import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    FormFeedback,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
    createUser,
    updateUser,
    getUser,
    getAllRoles,
} from "../../services/ValitanApiService";
import { enqueueSnackbar } from "notistack";

const UsersForm = () => {
    const [formData, setFormData] = useState({
        first_name: "",
        middle_name: "",
        last_name: "",
        mobile_number: "",
        email: "",
        address: "",
        roles: [],
        sex: "",
        nin: "",
        tin: "",
        certificate_number: "",
        company: "",
        is_valuer: "No",
        is_entity: "No",
    });

    const [errors, setErrors] = useState({});
    const [roles, setRoles] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { userId } = useParams();

    const BRAND_BLUE = "#0147C8";
    const HOVER_BLUE = "#0137A0";

    // Fetch roles
    useEffect(() => {
        (async () => {
            try {
                const { data } = await getAllRoles(0, 100);
                setRoles(Array.isArray(data?.content) ? data.content : []);
            } catch {
                enqueueSnackbar("Error fetching roles", { variant: "error" });
            }
        })();
    }, []);

    // Load user for editing
    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const { data } = await getUser(userId);
                setFormData({
                    first_name: data.first_name || "",
                    middle_name: data.middle_name || "",
                    last_name: data.last_name || "",
                    mobile_number: data.mobile_number || "",
                    email: data.email || "",
                    address: data.address || "",
                    sex: data.sex || "",
                    nin: data.nin || "",
                    tin: data.tin || "",
                    certificate_number: data.certificate_number || "",
                    company: data.company || "",
                    is_valuer: data.is_valuer ? "Yes" : "No",
                    is_entity: data.is_entity ? "Yes" : "No",
                    roles: data.roles?.map((r) => r.id) || [],
                });
                setIsEditing(true);
            } catch {
                enqueueSnackbar("Error loading user data!", { variant: "error" });
            }
        })();
    }, [userId]);

    // Mobile Number: Only 10 digits allowed
    const handleMobileChange = (e) => {
        let value = e.target.value.replace(/\D/g, "").slice(0, 10); // Only digits, max 10
        setFormData((prev) => ({ ...prev, mobile_number: value }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "is_valuer") {
            if (value === "No") {
                setFormData((prev) => ({
                    ...prev,
                    is_valuer: "No",
                    is_entity: "No",
                    certificate_number: "",
                    tin: "",
                    company: "",
                }));
            } else {
                setFormData((prev) => ({ ...prev, is_valuer: "Yes" }));
            }
            return;
        }

        if (name === "is_entity") {
            if (value === "No") {
                setFormData((prev) => ({ ...prev, is_entity: "No", tin: "", company: "" }));
            } else {
                setFormData((prev) => ({ ...prev, is_entity: "Yes" }));
            }
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (selected) => {
        setFormData((prev) => ({
            ...prev,
            roles: selected ? selected.map((opt) => opt.value) : [],
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        ["first_name", "middle_name", "last_name", "email", "sex"].forEach((f) => {
            if (!formData[f]) newErrors[f] = `${f.replace(/_/g, " ")} is required`;
        });

        if (!formData.mobile_number || formData.mobile_number.length !== 10) {
            newErrors.mobile_number = "Mobile number must be exactly 10 digits";
        }

        if (formData.roles.length === 0) newErrors.roles = "Select at least one role";

        if (formData.is_valuer === "Yes" && formData.is_entity === "No" && !formData.certificate_number) {
            newErrors.certificate_number = "Certificate number is required";
        }

        if (formData.is_valuer === "Yes" && formData.is_entity === "Yes") {
            if (!formData.tin) newErrors.tin = "TIN is required";
            if (!formData.company) newErrors.company = "Company name is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = {
                ...formData,
                is_valuer: formData.is_valuer === "Yes",
                is_entity: formData.is_entity === "Yes",
                id: isEditing ? userId : undefined,
            };

            Object.keys(payload).forEach((key) => {
                if (payload[key] === "") payload[key] = null;
            });

            if (isEditing) {
                await updateUser(userId, payload);
                enqueueSnackbar("User updated successfully", { variant: "success" });
            } else {
                await createUser(payload);
                enqueueSnackbar("User created successfully", { variant: "success" });
            }
            navigate("/users");
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Error saving user", { variant: "error" });
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Users Management" breadcrumbItem={isEditing ? "Edit User" : "Add User"} />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    {/* Basic Info */}
                                    <Row>
                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>First Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    name="first_name"
                                                    value={formData.first_name}
                                                    onChange={handleChange}
                                                    invalid={!!errors.first_name}
                                                />
                                                <FormFeedback>{errors.first_name}</FormFeedback>
                                            </FormGroup>
                                        </Col>

                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>Middle Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    name="middle_name"
                                                    value={formData.middle_name}
                                                    onChange={handleChange}
                                                    invalid={!!errors.middle_name}
                                                />
                                                <FormFeedback>{errors.middle_name}</FormFeedback>
                                            </FormGroup>
                                        </Col>

                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>Last Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    name="last_name"
                                                    value={formData.last_name}
                                                    onChange={handleChange}
                                                    invalid={!!errors.last_name}
                                                />
                                                <FormFeedback>{errors.last_name}</FormFeedback>
                                            </FormGroup>
                                        </Col>

                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>Email <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    disabled={isEditing}
                                                    invalid={!!errors.email}
                                                />
                                                <FormFeedback>{errors.email}</FormFeedback>
                                            </FormGroup>
                                        </Col>

                                        {/* Mobile Number - Special */}
                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>Mobile Number <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="tel"
                                                    name="mobile_number"
                                                    value={formData.mobile_number}
                                                    onChange={handleMobileChange}
                                                    placeholder="0712345678"
                                                    maxLength={10}
                                                    invalid={!!errors.mobile_number}
                                                />
                                                <FormFeedback>{errors.mobile_number}</FormFeedback>
                                                <small className={formData.mobile_number.length === 10 ? "text-success" : "text-muted"}>
                                                    {formData.mobile_number.length}/10 digits
                                                </small>
                                            </FormGroup>
                                        </Col>

                                        <Col lg={4} className="mb-3">
                                            <FormGroup>
                                                <Label>NIN</Label>
                                                <Input
                                                    name="nin"
                                                    value={formData.nin}
                                                    onChange={handleChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Sex & Valuer */}
                                    <Row className="mb-3">
                                        <Col lg={4}>
                                            <FormGroup>
                                                <Label>Sex <span className="text-danger">*</span></Label>
                                                <Input type="select" name="sex" value={formData.sex} onChange={handleChange} invalid={!!errors.sex}>
                                                    <option value="">Select Sex</option>
                                                    <option>Male</option>
                                                    <option>Female</option>
                                                    <option>Other</option>
                                                </Input>
                                                <FormFeedback>{errors.sex}</FormFeedback>
                                            </FormGroup>
                                        </Col>

                                        <Col lg={4}>
                                            <FormGroup>
                                                <Label>Is Valuer?</Label>
                                                <Input type="select" name="is_valuer" value={formData.is_valuer} onChange={handleChange}>
                                                    <option value="No">No</option>
                                                    <option value="Yes">Yes</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        {formData.is_valuer === "Yes" && (
                                            <Col lg={4}>
                                                <FormGroup>
                                                    <Label>Is Entity / Company?</Label>
                                                    <Input type="select" name="is_entity" value={formData.is_entity} onChange={handleChange}>
                                                        <option value="No">No (Individual)</option>
                                                        <option value="Yes">Yes (Company)</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Valuer Fields */}
                                    {formData.is_valuer === "Yes" && (
                                        <Row className="mb-3">
                                            {formData.is_entity === "No" && (
                                                <Col lg={4}>
                                                    <FormGroup>
                                                        <Label>Certificate Number <span className="text-danger">*</span></Label>
                                                        <Input
                                                            name="certificate_number"
                                                            value={formData.certificate_number}
                                                            onChange={handleChange}
                                                            invalid={!!errors.certificate_number}
                                                        />
                                                        <FormFeedback>{errors.certificate_number}</FormFeedback>
                                                    </FormGroup>
                                                </Col>
                                            )}

                                            {formData.is_entity === "Yes" && (
                                                <>
                                                    <Col lg={4}>
                                                        <FormGroup>
                                                            <Label>TIN <span className="text-danger">*</span></Label>
                                                            <Input
                                                                name="tin"
                                                                value={formData.tin}
                                                                onChange={(e) => handleChange({ target: { name: "tin", value: e.target.value.toUpperCase() } })}
                                                                invalid={!!errors.tin}
                                                            />
                                                            <FormFeedback>{errors.tin}</FormFeedback>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <FormGroup>
                                                            <Label>Company Name <span className="text-danger">*</span></Label>
                                                            <Input
                                                                name="company"
                                                                value={formData.company}
                                                                onChange={handleChange}
                                                                invalid={!!errors.company}
                                                            />
                                                            <FormFeedback>{errors.company}</FormFeedback>
                                                        </FormGroup>
                                                    </Col>
                                                </>
                                            )}
                                        </Row>
                                    )}

                                    {/* Address */}
                                    <Row className="mb-3">
                                        <Col lg={12}>
                                            <FormGroup>
                                                <Label>Address</Label>
                                                <Input type="textarea" name="address" rows="3" value={formData.address} onChange={handleChange} />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Roles */}
                                    <Row className="mb-4">
                                        <Col lg={12}>
                                            <FormGroup>
                                                <Label>Roles <span className="text-danger">*</span></Label>
                                                <Select
                                                    isMulti
                                                    options={roles.map((r) => ({ value: r.id, label: r.name }))}
                                                    value={roles
                                                        .filter((r) => formData.roles.includes(r.id))
                                                        .map((r) => ({ value: r.id, label: r.name }))}
                                                    onChange={handleRoleChange}
                                                    placeholder="Select one or more roles..."
                                                />
                                                {errors.roles && <div className="text-danger mt-1">{errors.roles}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {/* Submit */}
                                    <Button
                                        type="submit"
                                        style={{ backgroundColor: BRAND_BLUE, border: "none" }}
                                        onMouseEnter={(e) => (e.target.style.backgroundColor = HOVER_BLUE)}
                                        onMouseLeave={(e) => (e.target.style.backgroundColor = BRAND_BLUE)}
                                    >
                                        {isEditing ? "Update User" : "Create User"}
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default UsersForm;